import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { CheckCircle, XCircle, ShieldAlert, Search, FileSearch } from 'lucide-react'
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue'
import { useFlaggedArtworks, useApproveAnalysis, useRejectAnalysis } from '../hooks/useAnalysis'
import { artworkApi } from '../api/artworkApi'
import { EmptyState } from '@/components/common/data-display/EmptyState'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { Pagination } from '@/components/common/navigation/Pagination'
import { AnalysisEvidenceDialog } from './AnalysisEvidenceDialog'
import { PAGINATION } from '@/config/constants'
import type { RiskLevel, SortKey } from '../api/analysisApi'

const RISK_FILTERS: { value: string; label: string }[] = [
  // 'all' (not an explicit level list) so rows whose analysis has no riskLevel
  // key still appear here rather than silently vanishing from the only view
  // that claims to show everything.
  { value: 'all', label: 'All risk levels' },
  { value: 'critical,high', label: 'High risk only' },
  { value: 'medium', label: 'Medium risk only' },
  { value: 'low', label: 'Low risk only' },
  { value: 'unknown', label: 'Unknown risk only' },
]

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'pending_review', label: 'Awaiting review' },
  { value: 'approved', label: 'Cleared' },
  { value: 'rejected', label: 'Held' },
  { value: 'all', label: 'Any review state' },
]

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'risk:desc', label: 'Highest risk first' },
  { value: 'risk:asc', label: 'Lowest risk first' },
  { value: 'analysis_date:desc', label: 'Newest analysis' },
  { value: 'analysis_date:asc', label: 'Oldest analysis' },
  { value: 'confidence:desc', label: 'Most confident' },
  { value: 'confidence:asc', label: 'Least confident' },
  { value: 'title:asc', label: 'Title A–Z' },
]

const RISK_BADGE: Record<string, string> = {
  critical: 'bg-red-600 text-white border-transparent',
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-green-100 text-green-800 border-green-200',
  unknown: 'bg-muted text-muted-foreground',
}

function RiskBadge({ riskLevel }: { riskLevel: RiskLevel | null }) {
  const level = riskLevel || 'unknown'
  return (
    <Badge variant="outline" className={RISK_BADGE[level] || RISK_BADGE.unknown}>
      <ShieldAlert className="mr-1 h-3 w-3" />
      {level} risk
    </Badge>
  )
}

export function CopyrightReviewList() {
  const [page, setPage] = useState(1)
  const [riskFilter, setRiskFilter] = useState(RISK_FILTERS[0].value)
  const [statusFilter, setStatusFilter] = useState('pending_review')
  const [sort, setSort] = useState('risk:desc')
  const [search, setSearch] = useState('')
  const [evidenceId, setEvidenceId] = useState<number | null>(null)
  const [rejectTarget, setRejectTarget] = useState<{ analysisId: number; title: string } | null>(
    null
  )
  const [rejectNotes, setRejectNotes] = useState('')
  const pageSize = PAGINATION.DEFAULT_PAGE_SIZE

  // Without this every keystroke is a query; 300ms matches ArtistAdminPage.
  const debouncedSearch = useDebouncedValue(search, 300)
  const [sortBy, sortDir] = sort.split(':') as [SortKey, 'asc' | 'desc']

  const { data, isLoading } = useFlaggedArtworks({
    page,
    limit: pageSize,
    riskLevel: riskFilter,
    adminStatus: statusFilter,
    search: debouncedSearch.trim() || undefined,
    sortBy,
    sortDir,
  })

  const { mutate: approve } = useApproveAnalysis()
  const { mutate: reject } = useRejectAnalysis()

  const submitReject = () => {
    if (!rejectTarget || !rejectNotes.trim()) return
    reject(
      { analysisId: rejectTarget.analysisId, notes: rejectNotes.trim() },
      { onSuccess: () => setRejectTarget(null) }
    )
    setRejectNotes('')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Artworks with a completed copyright/IP analysis. Search by title, artist, or artwork ID.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              placeholder="Search title, artist, or ID…"
              className="pl-9"
            />
          </div>

          <Select
            value={riskFilter}
            onValueChange={(v) => {
              setRiskFilter(v)
              setPage(1)
            }}
          >
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RISK_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v)
              setPage(1)
            }}
          >
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={sort}
            onValueChange={(v) => {
              setSort(v)
              setPage(1)
            }}
          >
            <SelectTrigger className="w-full sm:w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {data && (
          <p className="text-xs text-muted-foreground">
            {data.pagination.total} result{data.pagination.total === 1 ? '' : 's'}
            {debouncedSearch.trim() && <> for “{debouncedSearch.trim()}”</>}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !data || data.artworks.length === 0 ? (
        <EmptyState
          title="Nothing flagged"
          description="No artworks are currently pending copyright review for this filter."
        />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {data.artworks.map((item) => (
              <Card key={item.analysis_id} className="overflow-hidden">
                <div
                  className="aspect-square cursor-pointer bg-muted"
                  onClick={() => setEvidenceId(item.analysis_id)}
                  title="View the evidence behind this assessment"
                >
                  <img
                    src={artworkApi.getArtworkImageUrl(item.artwork_id)}
                    alt={item.title || 'Artwork'}
                    className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                    onError={(e) => {
                      if (e.currentTarget.src.includes('placeholder-image')) return
                      e.currentTarget.src = '/placeholder-image.svg'
                    }}
                  />
                </div>
                <CardHeader className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-lg truncate">
                      {item.title || item.file_name}
                    </CardTitle>
                    <RiskBadge riskLevel={item.risk_level} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {item.artistic_name || item.artist_name}
                    {item.risk_confidence != null && (
                      <span> · {Math.round(item.risk_confidence * 100)}% confidence</span>
                    )}
                  </p>
                  {item.risk_reasoning && (
                    <p className="text-xs text-muted-foreground line-clamp-3">
                      {item.risk_reasoning}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setEvidenceId(item.analysis_id)}
                    className="flex items-center gap-1.5 text-xs font-medium text-foreground hover:underline"
                  >
                    <FileSearch className="h-3.5 w-3.5" />
                    View evidence
                  </button>
                </CardHeader>
                <CardContent className="flex gap-2">
                  <Button
                    onClick={() => approve({ analysisId: item.analysis_id })}
                    className="flex-1"
                    size="sm"
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Clear
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setRejectTarget({
                        analysisId: item.analysis_id,
                        title: item.title || item.file_name || `Artwork ${item.artwork_id}`,
                      })
                    }
                    className="flex-1"
                    size="sm"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Hold
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {data.pagination.total > pageSize && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data.pagination.total}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hold "{rejectTarget?.title}" for copyright concerns</DialogTitle>
            <DialogDescription>
              Notes are required — they're what the artist and future reviewers see.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            placeholder="Why is this being held?"
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={submitReject} disabled={!rejectNotes.trim()}>
              Hold artwork
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AnalysisEvidenceDialog
        analysisId={evidenceId}
        onOpenChange={(open) => !open && setEvidenceId(null)}
      />
    </div>
  )
}
